import React, { useState, ChangeEvent } from 'react';
import {
  Typography,
  Card,
  Grid,
  Divider,
  FormControl,
  TextField,
  Avatar,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { blue } from '@mui/material/colors';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { updateUser } from '../../../lib/api/userActions';
import { User } from '../../../lib/types.d';
import { useMutation, useQueryClient } from 'react-query';

interface Props {
  provider: any;
  classes: { heading: string; details: string };
  country: string;
  token: string;
  showPopup: (msg: string) => void;
}

const GeneralInformationPanel = ({ provider, classes, country, token, showPopup }: Props) => {
  const queryClient = useQueryClient();

  if (!provider || !provider.id) {
    return <>No provider found</>;
  }
  const id = provider.id;

  // Form Controls
  const [practiceAddress, setPracticeAddress] = useState(provider.practiceAddress);
  const [fullTitleName, setFullTitleName] = useState(provider.fullTitleName);
  const [phoneNumber, setPhoneNumber] = useState(provider.phoneNumber);

  // CA License Accordion Expansion Control
  const [expanded, setExpanded] = useState('false');

  const handleCALicenseExpansion = (panel: string) => (e: ChangeEvent<{}>, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : 'false');
  };

  // Provider Information Mutation Call
  const { mutateAsync: mutateProviderInformation } = useMutation(
    async () => {
      const providerInformation = {
        practiceAddress: practiceAddress,
        phoneNumber: phoneNumber,
        fullTitleName: fullTitleName,
      };
      await updateUser(token, providerInformation);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('providerInformation');
        showPopup('success');
      },
      onError: (err) => {
        console.log(err);
        showPopup('Something Went Wrong, Try Again.');
      },
    },
  );

  return (
    <Grid
      className={'card_container'}
      item
      xs={11}
    >
      <Card className="contactCard">
        <Typography
          className={'patient_name'}
          variant="h5"
          style={{ marginBottom: 8 }}
        >
          <Avatar style={{ display: 'inline-flex', backgroundColor: blue[700] }}>
            <AccountCircleIcon />
          </Avatar>{' '}
          General Prefill Information
        </Typography>
        <Divider />
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={0}
          style={{ paddingTop: 0, marginTop: 10 }}
        >
          <Grid
            item
            xs={4}
            style={{ textAlign: 'center' }}
          >
            <FormControl
              className={'patient_search_box'}
              style={{ width: 300, margin: 'auto', marginLeft: 10 }}
            >
              <TextField
                label="Title & Name"
                placeholder="Dr. Smith"
                key={id ? 'full_title_name_' + id : 'full_title_name'}
                value={fullTitleName}
                margin="normal"
                variant="outlined"
                fullWidth={true}
                onChange={(e) => {
                  setFullTitleName(e.target.value);
                }}
              />
            </FormControl>

            <FormControl
              className={'patient_search_box'}
              style={{ width: 300, margin: 'auto', marginLeft: 10 }}
            >
              <TextField
                label="Practice Address"
                placeholder="123 Main St."
                key={id ? 'practice_address' + id : 'practice_address'}
                value={practiceAddress}
                margin="normal"
                variant="outlined"
                fullWidth={true}
                onChange={(e) => {
                  setPracticeAddress(e.target.value);
                }}
              />
            </FormControl>

            <FormControl
              className={'patient_search_box'}
              style={{ width: 300, margin: 'auto', marginLeft: 10 }}
            >
              <TextField
                label="Phone Number"
                key={id ? 'phonenum' + id : 'phonenum'}
                placeholder={'1-345-6789'}
                value={phoneNumber}
                name="phone"
                margin="normal"
                variant="outlined"
                fullWidth={true}
                onChange={(e) => {
                  const onlyNums = e.target.value.replace(/[^0-9]/g, '');
                  if (onlyNums.length < 11) {
                    setPhoneNumber(onlyNums);
                  } else if (onlyNums.length === 11) {
                    const number = onlyNums.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
                    setPhoneNumber(onlyNums);
                  }
                }}
              />
            </FormControl>
          </Grid>

          <Grid
            style={{ margin: '0px auto 20px' }}
            container
            direction="row"
            justifyContent="space-around"
            alignItems="center"
            spacing={0}
          >
            <FormControl
              className={'patient_search_box'}
              style={{}}
            >
              <Button
                onClick={() => {
                  mutateProviderInformation();
                }}
                variant="contained"
                color="primary"
                style={{ marginTop: 10, marginBottom: 8 }}
              >
                Save Details &nbsp;
                <SaveIcon />
              </Button>
            </FormControl>
          </Grid>
        </Grid>
      </Card>
    </Grid>
  );
};

export default GeneralInformationPanel;
